import { apiGet } from './api';
import { pic } from './site';

/**
 * The public hotel feed. Separate from the admin helpers in `hotels.js` on purpose —
 * these endpoints return the sell price only, never our cost or margin.
 */

const withImage = (h) => ({
  ...h,
  img: h.imageUrl || pic(`${(h.cityName || h.countryName || 'hotel').toLowerCase()},resort,hotel`, 200 + (h.id % 60)),
});

export const fetchHotels = async (destinationId) => {
  const q = destinationId ? `?destinationId=${destinationId}` : '';
  const list = await apiGet(`/api/hotels/public${q}`);
  return (list || []).map(withImage);
};

export const fetchHotel = async (id) => withImage(await apiGet(`/api/hotels/public/${id}`));

/**
 * What a specific stay costs. Children go as ages, not a count — the supplier prices age bands,
 * and the bands are each hotel's own.
 */
export const fetchHotelQuote = (id, { stayFrom, stayTo, adults, childAges = [] }) => {
  const p = new URLSearchParams({ stayFrom, stayTo, adults: String(adults) });
  if (childAges.length) p.set('children', childAges.join(','));
  return apiGet(`/api/hotels/public/${id}/quote?${p.toString()}`);
};

/** "2 adults, 1 child (5)" — how the party reads back to the person who chose it. */
export const partyLine = (adults, childAges = []) => {
  const bits = [`${adults} adult${adults === 1 ? '' : 's'}`];
  if (childAges.length) {
    bits.push(`${childAges.length} child${childAges.length === 1 ? '' : 'ren'} (${childAges.join(', ')})`);
  }
  return bits.join(', ');
};

/** "20–27 Dec 2026" for the dates a visitor picked. */
export const stayLine = (from, to) => {
  const d = (v) => { const x = new Date(v); return Number.isNaN(x.getTime()) ? null : x; };
  const a = d(from), b = d(to);
  if (!a || !b) return '';
  const day = (x) => x.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return a.getFullYear() === b.getFullYear()
    ? `${day(a)} – ${day(b)} ${b.getFullYear()}`
    : `${day(a)} ${a.getFullYear()} – ${day(b)} ${b.getFullYear()}`;
};

export const money = (n) => (n == null ? '—' : `$${Number(n).toLocaleString('en-US')}`);

/** "5*" is how the supplier writes it; "5-star" is how a customer reads it. */
export const stars = (starCategory) => {
  if (!starCategory) return null;
  const n = parseInt(starCategory, 10);
  return Number.isFinite(n) && n > 0 && n <= 5 ? '★'.repeat(n) : starCategory;
};

/** The one-line summary under a hotel name: who it sleeps and on what board. */
export const occupancyLine = (h) => {
  const bits = [];
  if (h.maxAdults > 0) bits.push(`Sleeps up to ${h.maxAdults} adult${h.maxAdults === 1 ? '' : 's'}`);
  bits.push(h.acceptsChildren ? 'children welcome' : 'adults only');
  if (h.mealPlans?.length) bits.push(h.mealPlans.join(', ').toLowerCase());
  return bits.join(' · ');
};

export const fmtDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }); } catch { return ''; }
};
